import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import type {
	JiraIssueSearchScope,
	JiraIssueSearchSettings,
	JiraIssueSearchStaticViewInput,
} from '@atlassian/jira-timeline/src/__generated__/src_timelineQuery.graphql';
import timelineParameters from '@atlassian/jira-timeline/src/__generated__/src_timelineQuery$parameters';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import softwareTimelineParameters from './src/__generated__/JiraSoftwareTimelineAppQuery$parameters';

const NAMESPACE = 'TIMELINE';

const and = (clauses: string[]): string => {
	return clauses
		.map((clause) => clause.trim())
		.filter((clause) => clause.length > 0)
		.join(' AND ');
};

const getProjectJql = (projectKey: string) =>
	projectKey.length > 0 ? `project = '${projectKey}'` : '';

const buildJql = (projectKey = '') => {
	const project = getProjectJql(projectKey);
	return `${and([project])} ORDER BY rank ASC`;
};

// should be equivalent to the getToday function in jira/src/packages/software/roadmap/utils/src/utils/dates.tsx
const getToday = (): number => {
	const now = new Date();
	const date = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0));
	return date.valueOf();
};

const parseQueryParamValue = (str?: string): boolean | undefined => {
	try {
		return str ? JSON.parse(str) : undefined;
	} catch (e) {
		// Do nothing - ignore invalid values
	}
};

const toStaticViewInput = (settings: JiraIssueSearchSettings): JiraIssueSearchStaticViewInput => {
	return {
		isGroupingEnabled: !!settings.groupBy,
		isHideDoneEnabled: settings.hideDone,
		isHierarchyEnabled: settings.hierarchyEnabled,
	};
};

export const softwareTimelineEntrypoint = createEntryPoint({
	root: JSResourceForUserVisible(
		() =>
			import(
				/* webpackChunkName: "async-harmonised-software-timeline-entrypoint" */ './src/JiraSoftwareTimelineApp.tsx'
			),
	),
	getPreloadProps: ({ context, tenantContext: { cloudId } }: EntryPointRouteParams) => {
		const projectKey = context.match.params.projectKey;
		const boardId = context.match.params.boardId;
		const { hideDone } = context.query;

		if (projectKey == null) {
			throw new Error('Project key missing');
		}

		const prevFieldSetIds = ['issuetype', 'issuekey', 'summary', 'timeline'];
		const { fieldSetIds, fieldSetsInput } = fg('jira_timeline_sticky_assignee_status_columns')
			? {
					fieldSetIds: undefined,
					fieldSetsInput: {
						viewInput: {
							namespace: 'TIMELINE',
							viewId: 'timeline_default',
						},
					},
				}
			: {
					fieldSetIds: prevFieldSetIds,
					fieldSetsInput: {
						fieldSetIds: prevFieldSetIds,
					},
				};

		const scope: JiraIssueSearchScope | undefined = fg(
			'jira_timeline_sticky_assignee_status_columns',
		)
			? {
					operationScope: 'TIMELINE_PROJECT',
					projectKey,
				}
			: undefined;

		const jql = buildJql(projectKey);

		const settings: JiraIssueSearchSettings = {
			groupBy: null,
			hideDone: parseQueryParamValue(hideDone) ?? false,
			hierarchyEnabled: true,
			jql,
		};

		// JiraIssueSearchSettings will replace JiraIssueSearchStaticViewInput
		// as part of the saved views project, so we'll use it as the source of
		// truth
		const staticViewInput: JiraIssueSearchStaticViewInput = toStaticViewInput(settings);

		const sourceARI = createAri({
			resourceOwner: 'jira-software',
			cloudId,
			resourceType: 'board',
			resourceId: boardId ?? '',
		});

		return {
			extraProps: {
				today: getToday(),
				isTimelineHarmonisationM2Enabled: expValEquals(
					'timeline_harmonisation_m2',
					'isEnabled',
					true,
					false,
				),
			},
			queries: {
				softwareTimelineQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters: softwareTimelineParameters,
					variables: {
						cloudId,
						projectKey,
						sourceARI,
					},
				},
				timelineQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters: timelineParameters,
					variables: {
						amountOfColumns: 500,
						cloudId,
						fieldSetIds,
						fieldSetsInput,
						filterId: null,
						first: 50,
						issueSearchInput: { jql },
						namespace: NAMESPACE,
						projectKey,
						settings,
						shouldQueryFieldSetsById: false,
						shouldQueryHasChildren: true,
						scope,
						staticViewInput,
						viewConfigInput: { staticViewInput },
						viewQueryInput: {
							projectKeyAndItemIdQuery: {
								cloudId,
								projectKey,
								itemId: 'timeline',
							},
						},
						viewId: fg('jira_timeline_graphql_view_type') ? 'timeline_default' : 'list_default',
						isInlineEditingEnabled: fg('jira_timeline_sticky_assignee_status_columns'),
						// cleanup with jira_timeline_sticky_assignee_status_columns
						isTimelineStickyAssigneeStatusColumnEnabled: fg(
							'jira_timeline_sticky_assignee_status_columns',
						),
					},
				},
			},
		};
	},
});
